/**
 * Internal Hook: useMeetingActions
 *
 * This hook encapsulates all the memoized actions and utility functions
 * related to the scheduled meetings context. It helps to keep the main
 * provider component clean and focused on state aggregation.
 */

import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { SCHEDULED_MEETINGS_QUERY_KEYS } from '../helpers';
import type {
  GetAllMeetingsOptions,
  GetMeetingsOptions,
  ScheduledMeeting,
  ScheduledMeetingsApiService,
  ScheduledMeetingsProviderOptions,
  ScheduledMeetingsQuery,
} from '../types';

const DEFAULT_PAGE_SIZE = 2;

type UseMeetingActionsParams = {
  queryClient: QueryClient;
  isAuthenticated: boolean;
  api: ScheduledMeetingsApiService;
  options: ScheduledMeetingsProviderOptions;
  meetings: ScheduledMeeting[];
  refetch: () => Promise<void> | Promise<unknown>;
};

const useMeetingActions = ({
  queryClient,
  isAuthenticated,
  api,
  options,
  meetings,
  refetch,
}: UseMeetingActionsParams) => {
  const resolvePageSize = useCallback(
    (limit?: number | null) => {
      if (typeof limit === 'number' && limit > 0) {
        return limit;
      }
      return options.initialQuery?.limit && options.initialQuery.limit > 0
        ? options.initialQuery.limit
        : DEFAULT_PAGE_SIZE;
    },
    [options.initialQuery?.limit],
  );

  /**
   * Get meetings list (lazy)
   * Returns cached data if available, otherwise fetches
   */
  const getMeetings = useCallback(
    async (optionsOverride?: GetMeetingsOptions): Promise<ScheduledMeeting[]> => {
      if (!isAuthenticated) {
        return [];
      }

      const queryKey = SCHEDULED_MEETINGS_QUERY_KEYS.list(options.initialQuery);

      if (optionsOverride?.force) {
        await queryClient.invalidateQueries({
          queryKey,
        });
      }

      const cachedData = queryClient.getQueryData<InfiniteData<ScheduledMeeting[]>>(queryKey);
      if (cachedData && !optionsOverride?.force) {
        return cachedData.pages.flat();
      }

      const pageSize = resolvePageSize(options.initialQuery?.limit);

      const data = await queryClient.fetchInfiniteQuery({
        queryKey,
        initialPageParam: 0,
        queryFn: async ({ pageParam = 0 }) => {
          const response = await api.fetchMeetingsList({
            ...options.initialQuery,
            start: pageParam,
            limit: pageSize,
          });
          return response as ScheduledMeeting[];
        },
        getNextPageParam: (lastPage: ScheduledMeeting[], allPages: ScheduledMeeting[][]) => {
          if (lastPage.length < pageSize) {
            return undefined;
          }
          return allPages.reduce<number>((acc, page) => acc + page.length, 0);
        },
        staleTime: options.staleTime,
      });

      return data.pages.flat();
    },
    [api, isAuthenticated, options.initialQuery, options.staleTime, queryClient, resolvePageSize],
  );

  const getAllMeetingsFlat = useCallback(
    async (flatOptions?: GetAllMeetingsOptions): Promise<ScheduledMeeting[]> => {
      if (!isAuthenticated) {
        return [];
      }

      const flatDetailed = flatOptions?.detailed ?? options.initialQuery?.detailed ?? undefined;

      const { limit: _limit, start: _start, ...baseInitialQuery } = options.initialQuery ?? {};
      const flatQuery: ScheduledMeetingsQuery = {
        ...baseInitialQuery,
        detailed: flatDetailed,
      };
      if (flatQuery.detailed === undefined) {
        delete flatQuery.detailed;
      }

      const queryKey = SCHEDULED_MEETINGS_QUERY_KEYS.flat(flatQuery);

      if (flatOptions?.force) {
        await queryClient.invalidateQueries({
          queryKey,
        });
      }

      const cachedData = queryClient.getQueryData<ScheduledMeeting[]>(queryKey);
      if (cachedData && !flatOptions?.force) {
        return cachedData;
      }

      const data = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await api.fetchMeetingsList(flatQuery);
          return response as ScheduledMeeting[];
        },
        staleTime: options.staleTime,
      });

      return data;
    },
    [api, isAuthenticated, options.initialQuery, options.staleTime, queryClient],
  );

  /**
   * Get single meeting details
   */
  const getMeeting = useCallback(
    async (room: string): Promise<ScheduledMeeting | null> => {
      if (!isAuthenticated) {
        throw new Error('Authentication required');
      }

      const detailedPreference = options.initialQuery?.detailed ?? undefined;

      try {
        const data = await queryClient.fetchQuery({
          queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.detail(room),
          queryFn: async () => {
            const response = await api.fetchMeetingDetail(room, detailedPreference);

            if (!response || typeof response !== 'object') {
              throw new Error('Invalid meeting data');
            }

            const meeting = response as ScheduledMeeting;
            const isOwned = meetings.some((existing) => existing.room === meeting.room);

            return {
              ...meeting,
              ...(isOwned ? { isOwned: true } : {}),
            } as ScheduledMeeting;
          },
          staleTime: options.staleTime,
        });
        return data;
      } catch {
        return null;
      }
    },
    [
      api,
      isAuthenticated,
      meetings,
      options.initialQuery?.detailed,
      options.staleTime,
      queryClient,
    ],
  );

  /**
   * Refresh meetings (force refetch)
   */
  const refreshMeetings = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.lists(),
    });
    await refetch();
  }, [queryClient, refetch]);

  /**
   * Get meeting by room name from cache
   */
  const getMeetingByRoom = useCallback(
    (room: string): ScheduledMeeting | null => {
      return meetings.find((m) => m.room === room) || null;
    },
    [meetings],
  );

  /**
   * Check if meeting is owned by current user
   */
  const isMeetingOwned = useCallback(
    (room: string): boolean => {
      return meetings.some((m) => m.room === room);
    },
    [meetings],
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    queryClient.resetQueries({
      queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.all,
    });
  }, [queryClient]);

  /**
   * Invalidate meetings cache
   */
  const invalidateMeetings = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.all,
    });
  }, [queryClient]);

  return {
    getMeetings,
    getAllMeetingsFlat,
    getMeeting,
    refreshMeetings,
    getMeetingByRoom,
    isMeetingOwned,
    clearError,
    invalidateMeetings,
  };
};

export default useMeetingActions;
